import { useState } from 'react'

import { useUpdateStoreIncludeOrderNowInReportsMutation } from '@/dealers/_gen/gql'
import useReloadSession from '@/dealers/hooks/useReloadSession'
import useSession from '@/dealers/hooks/useSession'
import Action from '@/gf/components/Action'
import Checkbox from '@/gf/components/Checkbox'
import useMsgs from '@/gf/hooks/useMsgs'
import { H2 } from '../WorkspaceSettings'

export default function IncludeOrderNowInReports() {
  const {
    store: { includeOrderNowInReports },
    storeId,
  } = useSession()
  const reloadSession = useReloadSession()
  const [_msgs, msgsMgr] = useMsgs()
  const [updateMutation, { loading }] = useUpdateStoreIncludeOrderNowInReportsMutation({
    onCompleted: () => {
      msgsMgr.add('Reports settings updated', 'positive')
      reloadSession()
    },
    onError: (error) => {
      msgsMgr.add(error?.message || 'Error updating reports settings', 'negative')
    },
  })

  const defaultEnabled = includeOrderNowInReports ?? true
  const [enabled, setEnabled] = useState(() => defaultEnabled)
  const editing = enabled !== defaultEnabled

  return (
    <div className="px-8 py-6 last:pb-0">
      <H2 id="include-order-now-in-reports">Include Order Now Requests in Reports</H2>
      <p className="text-sm mt-6">
        When a buyer uses Gearflow Order Now, their request goes straight to a PO without going
        through your team's quoting workflow. By default, those requests are counted alongside your
        sales team's quoted work in the Performance and Service reports.
      </p>
      <p className="text-sm">
        Turn this off if you'd rather have those reports reflect only requests your sales team
        actually quoted. Order Now requests still appear on the Requests page — they just won't be
        counted in the conversion rate, activity chart, or user and customer breakdowns.
      </p>

      <div className="flex flex-col gap-3 mt-6 max-w-sm">
        <Checkbox
          label="Include Order Now requests in reports"
          className="w-max"
          checked={enabled}
          onChange={() => setEnabled((prev) => !prev)}
        />
      </div>
      {editing && (
        <div className="flex gap-2 mt-4 max-w-sm justify-end">
          <Action.S onClick={() => setEnabled(defaultEnabled)}>Cancel</Action.S>
          <Action.P
            performing={loading}
            disabled={loading}
            type="submit"
            onClick={() =>
              updateMutation({
                variables: {
                  storeId,
                  includeOrderNowInReports: enabled,
                },
              })
            }
          >
            Save
          </Action.P>
        </div>
      )}
    </div>
  )
}
